import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  SummaryCardHorizontalItem,
  SummaryCardHorizontalComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { AgrDatePipe, LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { MIN_FLOCKS_FOR_BENCHMARK } from 'src/app/shared/constants';
import { TenantAwareTranslationService } from 'src/app/shared/services/tenant-aware-translation.service';

import {
  Benchmark,
  BenchmarkData,
  BenchmarkSettings,
  BenchmarkTypes,
  DataViewType,
  FarmerInsightsModuleState,
  FlockDataParameter,
  NormalizedTimeSeries,
} from '../../../models/farmer-insights.model';
import {
  getBenchmarkCustomerAveragSelectedPeriodData,
  getBenchmarkLastWeeksData,
  getBenchmarkSelectedPeriodData,
  getBenchmarkSettings,
  getNormalizedTimeSeriesArray,
  getSelectedDataViewType,
  getSelectedKpis,
} from '../../../store/selectors/farmer-insights.selectors';

@Component({
  selector: 'app-data-view-table-simple',
  templateUrl: './data-view-table-simple.component.html',
  styleUrls: ['./data-view-table-simple.component.scss'],
  imports: [NgIf, SummaryCardHorizontalComponent, NgFor, NotificationEmptyComponent, AsyncPipe],
})
export class DataViewTableSimpleComponent {
  private readonly store = inject<Store<FarmerInsightsModuleState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly tenantTranslation = inject(TenantAwareTranslationService);
  private readonly datePipe = inject(AgrDatePipe);

  public normalizedTimeSeriesArray$: Observable<NormalizedTimeSeries[]> = this.store.select(
    getNormalizedTimeSeriesArray,
  );
  public selectedDataViewType$: Observable<DataViewType> =
    this.store.select(getSelectedDataViewType);
  public benchmarkSelectedPeriodData$: Observable<BenchmarkData> = this.store.select(
    getBenchmarkSelectedPeriodData,
  );
  public benchmarkLastWeeksData$: Observable<BenchmarkData> =
    this.store.select(getBenchmarkLastWeeksData);
  public benchmarkCustomerAveragSelectedPeriodData$: Observable<BenchmarkData> = this.store.select(
    getBenchmarkCustomerAveragSelectedPeriodData,
  );

  public selectedKpis$: Observable<FlockDataParameter[]> = this.store.select(getSelectedKpis);

  public benchmarkSettings$: Observable<BenchmarkSettings> =
    this.store.select(getBenchmarkSettings);
  public benchmarkSettings: BenchmarkSettings = null;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.benchmarkSettings$.pipe(take(1)).subscribe((settings) => {
      this.benchmarkSettings = settings;
    });
  }

  getTitle(ts: NormalizedTimeSeries, dataViewType: DataViewType): string {
    switch (dataViewType) {
      case DataViewType.PerCycleBarn:
        return (
          `${ts.locationName ?? ''} </br>` +
          `${this.locale.translate('cycle')}: ${ts.cycleNumber ?? ''} </br>` +
          `${this.locale.translate('barn')}: ${ts.barnName ?? ''}  </br>` +
          `${this.locale.translate('breed')}: ${ts.breeds ?? ''}  </br>` +
          `<small>${ts.fromDate ? this.datePipe.transform(ts.fromDate) : ''} - ${
            ts.toDate ? this.datePipe.transform(ts.toDate) : ''
          }</small>`
        );
      case DataViewType.PerCycle:
        return (
          `${ts.locationName ?? ''} - ${this.locale.translate('cycle')}: ${
            ts.cycleNumber ?? ''
          } </br> ` +
          `${this.locale.translate('breed')}: ${ts.breeds ?? ''}` +
          `</br><small>` +
          `${ts.fromDate ? this.datePipe.transform(ts.fromDate) : ''} - ${
            ts.toDate ? this.datePipe.transform(ts.toDate) : ''
          }</small>`
        );
      case DataViewType.PerLocation:
        return (
          `${ts.locationName ?? ''} </br>` + `${this.locale.translate('breed')}: ${ts.breeds ?? ''}`
        );
      case DataViewType.PerLocationBarn:
        return (
          `${ts.locationName ?? ''} </br>` +
          `${this.locale.translate('barn')}: ${ts.barnName ?? ''}  </br>` +
          `${this.locale.translate('breed')}: ${ts.breeds ?? ''}  </br>` +
          `<small>${ts.fromDate ? this.datePipe.transform(ts.fromDate) : ''} - ${
            ts.toDate ? this.datePipe.transform(ts.toDate) : ''
          }</small>`
        );
    }
  }

  getSummaryCardItems(timeSeries: NormalizedTimeSeries) {
    const kpiItems: SummaryCardHorizontalItem[] = [];
    timeSeries.kpiValues.forEach((kpi) => {
      const label = this.locale.translate('farmer-insights.kpis.' + kpi.parameter.code);
      if (kpi.value === undefined || Number.isNaN(kpi.value) || !Number.isFinite(kpi.value)) {
        kpiItems.push({
          label,
          value: this.locale.translate('n/a'),
          unit: '',
          decimalPrecision: 0,
          prefixUnit: false,
        });
      } else {
        kpiItems.push({
          label,
          value: kpi.value,
          unit: kpi.parameter.unit,
          decimalPrecision: kpi.parameter.numberOfDecimals,
          prefixUnit: <boolean>(<unknown>kpi.parameter.properties['PrefixUnit']),
        });
      }
    });
    return kpiItems;
  }

  getBenchmarkTitle(
    benchmarkSelectedPeriodData: BenchmarkData,
    dataViewType: DataViewType,
    benchmarkSettings: BenchmarkSettings,
  ): string {
    let benchmarkName = '';
    switch (benchmarkSettings.selectedNonDetailedBenchmarkType) {
      case BenchmarkTypes.LastWeeks:
        benchmarkName = `${this.locale.translate('farmer-insights.benchmark-last-weeks')}</br>`;
        break;
      case BenchmarkTypes.SelectedPeriod:
        benchmarkName = `${this.locale.translate(
          'farmer-insights.benchmark-selected-period',
        )}</br>`;
        break;
      case BenchmarkTypes.CustomerAverageSelectedPeriod:
        benchmarkName = this.locale.translate('farmer-insights.customer-average-selected-period');
        break;
    }

    switch (dataViewType) {
      case DataViewType.PerCycleBarn:
        return `${this.tenantTranslation.translate('farmer-insights.benchmark-title')}
        ${benchmarkName}
        (${benchmarkSelectedPeriodData?.flocksUsed})</br></br></br></br>`;
      case DataViewType.PerCycle:
        return `${this.tenantTranslation.translate('farmer-insights.benchmark-title')}
        ${benchmarkName}
        (${benchmarkSelectedPeriodData?.flocksUsed})</br></br>`;
      case DataViewType.PerLocation:
        return `${this.tenantTranslation.translate('farmer-insights.benchmark-title')}
        ${benchmarkName}
        (${benchmarkSelectedPeriodData?.flocksUsed})</br>`;
      case DataViewType.PerLocationBarn:
        return `${this.tenantTranslation.translate('farmer-insights.benchmark-title')}
          ${benchmarkName}
          (${benchmarkSelectedPeriodData?.flocksUsed})</br></br></br>`;
    }
  }

  getBenchmarks(
    timeSeriesArray: NormalizedTimeSeries[],
    benchmarkData: BenchmarkData,
    considerMinFlocksForBenchmark: boolean,
  ) {
    const kpiItems: SummaryCardHorizontalItem[] = [];
    if (timeSeriesArray.length > 0) {
      const timeSeries = timeSeriesArray[0];
      const retrievedKpis = timeSeries.kpiValues.map((k) => k.parameter.code);
      if (retrievedKpis && benchmarkData) {
        if (considerMinFlocksForBenchmark && benchmarkData?.flocksUsed < MIN_FLOCKS_FOR_BENCHMARK) {
          kpiItems.push({
            label: this.locale.translate('farmer-insights.no-benchmark'),
            isEmptyPlaceholder: false,
          });
          // Add placeholders to make sure the card is the same length as the other KPI cards.
          for (let i = 0; i < retrievedKpis.length - 1; i++) {
            kpiItems.push({
              isEmptyPlaceholder: true,
            });
          }
        } else {
          retrievedKpis.forEach((kpi) => {
            // Check whether this KPI has a benchmark and add it if that's the case. Otherwise hide it.
            const benchmark: Benchmark = benchmarkData.benchmarks.find(
              (b) => b.parameter.code === kpi,
            );
            if (benchmark && isFinite(benchmark.value)) {
              kpiItems.push({
                label: this.locale.translate('farmer-insights.kpis.' + kpi),
                value: benchmark.value,
                unit: benchmark.parameter.unit,
                decimalPrecision: benchmark.parameter.numberOfDecimals,
                isEmptyPlaceholder: false,
              });
            } else {
              kpiItems.push({
                label: this.locale.translate('farmer-insights.kpis.' + kpi),
                value: NaN,
                unit: '',
                decimalPrecision: 0,
                isEmptyPlaceholder: true,
              });
            }
          });
        }
      }

      return kpiItems;
    }
  }
}
